import React, { useState } from "react";
import { API_URL } from "..";
import "bootstrap/dist/js/bootstrap.bundle.min.js"; // IMPORTANT

function formatDate(inputDate) {
  if (!inputDate) return "N/A";
  const date = new Date(inputDate);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export default function ViewSupplierLayer({ data }) {
  const [activeTab, setActiveTab] = useState("details");
  const [selectedBill, setSelectedBill] = useState(null);
  const [message, setMessage] = useState({ show: false, text: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const locationId = localStorage.getItem('locationID')

  if (!data) return <p>No supplier data available.</p>;

  // 🧮 Calculate totals from bills
  const bills = data?.PurchaseBills || [];

  // 👇 Direct values from Supplier record (no bills calculation!)
  const totalAmount = Number(data.total_amount || 0);
  const totalPaid = Number(data.paid || 0);
  const opening_balance = Number(data.opening_balance || 0);
  const totalDue = opening_balance + totalAmount - totalPaid;


  // ✅ Open modal using hidden button trigger (NO Bootstrap JS error)
  const openPaymentModal = (bill) => {
    setSelectedBill(bill);
    setMessage({ show: false });

    const trigger = document.getElementById("paymentModalTrigger");
    if (trigger) trigger.click(); // Opens modal successfully
  };

  // 💳 Submit Payment
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ show: false });

    const form = new FormData(e.target);

    const payload = {
      bill_id: selectedBill.id,
      supplier_id: selectedBill.supplier_id,
      payment_method: form.get("payment_method"),
      payment_date: form.get("payment_date"),
      amount: Number(form.get("amount")),
      remarks: form.get("remarks"),
      locationId: locationId,
    };

    try {
      const res = await fetch(`${API_URL}/api/purchase-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await res.json();

      if (res.ok) {
        setMessage({ show: true, text: "Payment added successfully!", type: "success" });

        setTimeout(() => {
          document.getElementById("closePaymentModalBtn")?.click();
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(body.message || "Payment failed");
      }
    } catch (err) {
      setMessage({ show: true, text: err.message, type: "danger" });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="card basic-data-table">

      {/* HIDDEN BUTTON FOR MODAL TRIGGER */}
      <button
        id="paymentModalTrigger"
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#paymentModal"
      ></button>

      <div className="card-header border-bottom pb-0 mb-3">
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              Supplier Details
            </button>
          </li>

          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "purchaseOrders" ? "active" : ""}`}
              onClick={() => setActiveTab("purchaseOrders")}
            >
              Purchase Orders
              {data.PurchaseOrders?.length ? (
                <span className="badge bg-primary-subtle ms-2">
                  {data.PurchaseOrders.length}
                </span>
              ) : null}
            </button>
          </li>

          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "bills" ? "active" : ""}`}
              onClick={() => setActiveTab("bills")}
            >
              Bills
              {data.PurchaseBills?.length ? (
                <span className="badge bg-primary-subtle ms-2">
                  {data.PurchaseBills.length}
                </span>
              ) : null}
            </button>
          </li>
        </ul>
      </div>

      <div className="card-body">

        {/* ================= DETAILS TAB ================= */}
        {activeTab === "details" && (
          <div className="row g-3 mt-2">

            <div className="col-md-6">
              <div className="p-3 border rounded bg-light-subtle">
                <h6 className="fw-bold  mb-3">Personal Information</h6>

                <p><strong>Name:</strong> {data.name}</p>
                <p><strong>Company:</strong> {data.company || "N/A"}</p>
                <p><strong>Email:</strong> {data.email}</p>
                <p><strong>Phone:</strong> {data.phone}</p>
                <p><strong>Address:</strong> {data.address}</p>
                <p><strong>City:</strong> {data.city}</p>
                <p><strong>Country:</strong> {data.country}</p>
              </div>
            </div>


            <div className="col-md-6">
              <div className="p-3 border rounded bg-light-subtle">
                <h6 className="fw-bold mb-3">Financial Details</h6>

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span><strong>Opening Balance:</strong> Rs {opening_balance.toLocaleString()}</span>
                  <button
                    className="btn btn-sm btn-outline-primary py-0 px-2"
                    onClick={() => openPaymentModal({ type: 'opening_balance', supplier_id: data.id })}
                  >
                    Pay
                  </button>
                </div>
                <p><strong>Total Amount:</strong> Rs {totalAmount.toLocaleString()}</p>
                <p><strong>Paid:</strong> Rs {totalPaid.toLocaleString()}</p>
                <p><strong>Balance:</strong> Rs {totalDue.toLocaleString()}</p>

                <p>
                  <strong>Status:</strong>
                  <span className={`badge ms-2 ${data.status === "active" ? "bg-success" : "bg-danger"}`}>
                    {data.status}
                  </span>
                </p>

                <p><strong>Added On:</strong> {formatDate(data.createdAt)}</p>
              </div>
            </div>

          </div>
        )}

        {/* ================= PURCHASE ORDERS TAB ================= */}
        {activeTab === "purchaseOrders" && (
          <div>
            {data.PurchaseOrders?.length ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>PO Number</th>
                      <th>Date</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.PurchaseOrders
                      .filter((e) => e.status !== "Billed")
                      .map((po) => (
                        <tr key={po.id}>
                          <td>
                            <a
                              href={`/view-po/${po.po_number}`}
                              className="text-primary"
                            >
                              {po.po_number}
                            </a>
                          </td>
                          <td>{formatDate(po.date)}</td>
                          <td>{Number(po.total_amount).toFixed(2)}</td>
                          <td>{po.status}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No purchase orders found.</p>
            )}
          </div>
        )}


        {/* ================= BILLS TAB ================= */}
        {activeTab === "bills" && (
          <div>
            {data.PurchaseBills?.length ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {data.PurchaseBills.map((bill) => (
                      <tr key={bill.id}>
                        <td>
                          <a
                            href={`/view-bill/${bill.id}`}
                            className="text-primary"
                          >
                            {bill.bill_number}
                          </a>
                        </td>
                        <td>{formatDate(bill.date)}</td>
                        <td>{Number(bill.total_amount).toFixed(2)}</td>
                        <td>{Number(bill.paid_amount).toFixed(2)}</td>
                        <td>{Number(bill.due_amount).toFixed(2)}</td>
                        <td>
                          <span className={`badge ${bill.status === "Unpaid" ? "bg-danger" : "bg-success"}`}>
                            {bill.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => openPaymentModal(bill)}
                            disabled={bill.status === "Paid"}
                          >
                            {bill.status === "Paid" ? "Paid" : "Pay"}
                          </button>

                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            ) : (
              <p>No bills found.</p>
            )}
          </div>
        )}

      </div>

      {/* ================= PAYMENT MODAL ================= */}
      <div className="modal fade" id="paymentModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">

            <form onSubmit={handlePaymentSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedBill && selectedBill.type === 'opening_balance'
                    ? "Pay Opening Balance"
                    : "Make Payment"
                  }
                </h5>
                <button id="closePaymentModalBtn" type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>

              <div className="modal-body">

                {message.show && (
                  <div className={`alert alert-${message.type}`}>{message.text}</div>
                )}

                {selectedBill ? (
                  <>
                    {selectedBill.type !== 'opening_balance' && (
                      <>
                        <div className="mb-3">
                          <label>Bill Number</label>
                          <input className="form-control" readOnly value={selectedBill.bill_number} />
                        </div>

                        <div className="d-flex gap-2 mb-3">
                          <div className="w-50">
                            <label>Total</label>
                            <input className="form-control" readOnly value={`Rs. ${selectedBill.total_amount}`} />
                          </div>
                          <div className="w-50">
                            <label>Due</label>
                            <input className="form-control" readOnly value={`Rs. ${selectedBill.due_amount}`} />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mb-3">
                      <label>Payment Method</label>
                      <select name="payment_method" className="form-select" required>
                        <option value="">Select</option>
                        <option value="Cash">Cash</option>
                        <option value="Bank">Bank</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label>Payment Date</label>
                      <input type="date" name="payment_date" className="form-control" required />
                    </div>

                    <div className="mb-3">
                      <label>Amount</label>
                      <input type="number" name="amount" className="form-control" required />
                    </div>

                    <div className="mb-3">
                      <label>Remarks</label>
                      <textarea name="remarks" className="form-control" rows="2"></textarea>
                    </div>
                  </>
                ) : "Loading..."}

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : "Make Payment"}
                </button>
              </div>

            </form>

          </div>
        </div>
      </div>

    </div>
  );
}
